import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";

const Container = styled.div`  margin-top: 1rem;
`;

const Label = styled.label`
  font-size: 16px;
  color: #333;
  display: block;
  margin-bottom: 10px;
`;

const Description = styled.div`
  font-weight:300;
  font-size:12px;
  margin-top:10px;
  font-family:'Raleway', sans-serif;
`;

const Qualifier = styled.div`
  font-size:16px;
  font-weight:bold;
  margin-bottom:10px;
`;

// New styled components for custom select
const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
  font-family: 'Raleway', sans-serif;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 10px 14px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  color: ${props => props.isPlaceholder ? '#888' : '#333'};
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  text-align: left;

  &:hover {
    border-color: #aaa;
  }

  &:focus {
    outline: none;
    border-color: #4f8be2;
    box-shadow: 0 0 0 2px rgba(79, 139, 226, 0.2);
  }
`;

const DropdownIcon = styled.div`
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.2s;
  
  &::after {
    content: '▼';
    font-size: 12px;
    color: #666;
  }
`;

const OptionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-top: 4px;
  padding: 0;
  list-style: none;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const Option = styled.li`
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #f5f5f5;
  }
  
  ${props => props.isSelected && `
    background: #edf4ff;
    font-weight: 500;
    
    &:hover {
      background: #e1ecfd;
    }
  `}
`;

export default function QuestionDropdown({ question, currentAnswer, onAnswerChange }) {
  const options = question.properties.options || [];
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(currentAnswer.response || "");
  const selectRef = useRef(null);

  const handleSelectOption = (option) => {
    setSelectedValue(option);
    onAnswerChange(question.uniqueId, { response: option });
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Container>
      {question.qualifier && <Qualifier>{question.qualifier}</Qualifier>}
      <Label>{question.question}</Label>
      {question.description && <Description>{question.description}</Description>}

      <SelectWrapper ref={selectRef}>
        <SelectButton 
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          isPlaceholder={!selectedValue}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          {selectedValue || "Select..."}
          <DropdownIcon isOpen={isOpen} />
        </SelectButton>
        
        <OptionsList isOpen={isOpen} role="listbox">
          {options.map((opt, idx) => (
            <Option 
              key={idx} 
              isSelected={selectedValue === opt}
              onClick={() => handleSelectOption(opt)}
              role="option"
              aria-selected={selectedValue === opt}
            >
              {opt}
            </Option>
          ))}
        </OptionsList>
      </SelectWrapper>
    </Container>
  );
}

